/**
 * @lines98/client/components/pill-button — переиспользуемый примитив кнопки-пилюли:
 * ведущая иконка + подпись, скруглённая, полноширинная. Два варианта вида:
 *   - 'accent' (default) — сплошной accent-фон, белый текст («Войти» / «Выйти»);
 *   - 'neutral' — фон в тон панели (--panel-2), тонкая рамка --line, тёмный текст
 *     --ink; различается ТОЛЬКО ведущей (часто цветной) иконкой (owner-decision
 *     2026-06-12 A3: кнопки провайдеров нейтральные, а не 3 accent-заливки).
 *
 * ЗАЧЕМ (owner-directive 2026-06-12 «через примитивы» + CLAUDE.md «ствол → ветки»):
 * заметная action-пилюля нужна на нескольких surface'ах (вход на рейле, выход в меню
 * чипа, 3 кнопки провайдеров в модалке). Это ОДИН примитив, а не копия вёрстки по
 * экранам — экран лишь задаёт иконку/подпись/действие/variant (+ опц. модификатор-
 * класс). Раньше «Войти» верстался вручную (.nav-item--login) — теперь общий примитив.
 *
 * Иконка — inline-SVG (innerHTML). Для accent-варианта currentColor наследует белый;
 * нейтральные провайдер-иконки несут СВОЙ цвет (Google многоцвет, Discord блюрпл,
 * Почта — accent-токен), задаётся внутри самого SVG вызывающим (A3). Все размеры —
 * токенами (--m / --fs-control / --r-sm / --shadow-md), без вписанных px (co-located
 * pill-button.css). Только структура + клик; вид целиком в CSS.
 */

import './pill-button.css';

export type PillButtonVariant = 'accent' | 'neutral';

export interface PillButtonOptions {
  /** Inline-SVG ведущей иконки (innerHTML). */
  readonly icon: string;
  /** Подпись кнопки. */
  readonly label: string;
  /**
   * Вид кнопки. 'accent' (default) — accent-заливка/белый текст («Войти»/«Выйти»);
   * 'neutral' — фон --panel-2 + рамка --line + текст --ink, отличается лишь иконкой.
   */
  readonly variant?: PillButtonVariant;
  /** Доступное имя (по умолчанию = label). */
  readonly ariaLabel?: string;
  /** Доп. класс на кнопку (surface-специфичные правки). */
  readonly className?: string;
  /** Действие при клике. */
  readonly onClick?: () => void;
}

export interface PillButtonHandle {
  readonly element: HTMLButtonElement;
}

/** Собрать accent-кнопку-пилюлю (иконка + подпись). Возвращает handle с `element`. */
export function createPillButton(opts: PillButtonOptions): PillButtonHandle {
  const btn = document.createElement('button');
  btn.type = 'button';
  // 'accent' — без модификатора (база-вид); 'neutral' — модификатор-класс.
  const variantClass = opts.variant === 'neutral' ? ' pill-button--neutral' : '';
  btn.className = `pill-button${variantClass}${opts.className ? ` ${opts.className}` : ''}`;
  btn.innerHTML = `
    <span class="pill-button-icon">${opts.icon}</span>
    <span class="pill-button-label">${escapeHtml(opts.label)}</span>
  `;
  if (opts.ariaLabel) btn.setAttribute('aria-label', opts.ariaLabel);
  if (opts.onClick) btn.addEventListener('click', opts.onClick);
  return { element: btn };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) =>
    ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&quot;',
  );
}
